// src/pages/DigitalSignature/DigitalSignatureView.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material"
import {
    Description,
    Person,
    AccessTime,
    CheckCircle,
    Download as DownloadIcon,
    ContentCopy,
    MoreVert,
    Cancel as CancelIcon,
} from "@mui/icons-material"
import { useNavigate, useParams } from "react-router-dom"
import { api } from "../../../services/Main/Api"
import moment from "moment"
import DocSignHistory from "../../../components/DocSignHistory"
import { useSnackbar } from "notistack"

const DOC_SIGN_DETAIL_ENDPOINT = (id) => `sign/doc-sign/${id}/`
const DOWNLOAD_ENDPOINT = (documentId) => `sign/download/${documentId}/`
const CANCEL_ENDPOINT = (documentId) => `sign/cancel/${documentId}/`

const safeStr = (v) => (v === null || v === undefined || v === "" ? "-" : String(v))

// tenta extrair filename do header Content-Disposition
const getFilenameFromDisposition = (disposition) => {
    if (!disposition) return null

    // filename="abc.pdf"
    const match1 = /filename="([^"]+)"/i.exec(disposition)
    if (match1?.[1]) return match1[1]

    // filename=abc.pdf
    const match2 = /filename=([^;]+)/i.exec(disposition)
    if (match2?.[1]) return match2[1].trim()

    return null
}

// ✅ monta url no padrão do PlugSign quando você tiver viewId + signingKey
const buildPlugSignViewUrl = (viewId, signingKey) => {
    if (!viewId) return ""
    const base = `https://app.plugsign.com.br/view/${viewId}`
    if (!signingKey) return base
    return `${base}?signingKey=${encodeURIComponent(signingKey)}`
}

export default function DigitalSignatureView({ apiClient }) {
    const client = apiClient || api
    const navigate = useNavigate()
    const { id } = useParams()
    const { enqueueSnackbar } = useSnackbar()

    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [error, setError] = useState("")
    const [docSign, setDocSign] = useState(null)

    // ✅ menu 3 pontinhos + confirmação cancelamento
    const [menuAnchor, setMenuAnchor] = useState(null)
    const menuOpen = Boolean(menuAnchor)
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
    const [cancelling, setCancelling] = useState(false)

    const openMenu = (e) => setMenuAnchor(e.currentTarget)
    const closeMenu = () => setMenuAnchor(null)

    const openCancelConfirm = () => {
        closeMenu()
        setConfirmCancelOpen(true)
    }
    const closeCancelConfirm = () => setConfirmCancelOpen(false)

    const fetchDetail = useCallback(async () => {
        if (!id) return
        setLoading(true)
        setError("")
        try {
            const res = await client.get(DOC_SIGN_DETAIL_ENDPOINT(id))
            setDocSign(res?.data || null)
        } catch (err) {
            setDocSign(null)
            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Não foi possível carregar os dados da assinatura."
            )
        } finally {
            setLoading(false)
        }
    }, [client, id])

    useEffect(() => {
        fetchDetail()
    }, [fetchDetail])

    const pendingSigners = useMemo(() => {
        const arr = docSign?.signers || []
        return Array.isArray(arr) ? arr : []
    }, [docSign])

    const handleDownload = useCallback(async () => {
        const documentId = docSign?.document_id
        if (!documentId) {
            setError("Este documento ainda não possui document_id para download.")
            return
        }

        setDownloading(true)
        setError("")
        try {
            const res = await client.get(DOWNLOAD_ENDPOINT(documentId), {
                responseType: "blob",
            })

            const blob = res?.data
            const disposition = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"]
            const filenameFromHeader = getFilenameFromDisposition(disposition)

            const safeBase = (docSign?.name || `documento-${documentId}`).replace(/[\\/:*?"<>|]+/g, "-")
            const filename = filenameFromHeader || `${safeBase}.pdf`

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch (err) {
            setError(err?.response?.data?.detail || err?.response?.data?.message || "Não foi possível baixar o arquivo.")
        } finally {
            setDownloading(false)
        }
    }, [client, docSign])

    // ✅ resolve a URL de visualização: tenta campos comuns + fallback para padrão PlugSign
    const resolveDocViewUrl = useCallback(() => {
        const direct =
            docSign?.view_url ||
            docSign?.url ||
            docSign?.public_url ||
            docSign?.sign_url ||
            docSign?.signing_url ||
            docSign?.plugsign_url

        if (direct) return String(direct)

        const viewId = docSign?.view_id || docSign?.plugsign_view_id || docSign?.document_view_id
        const signingKey = docSign?.signing_key || docSign?.plugsign_signing_key
        const built = buildPlugSignViewUrl(viewId, signingKey)

        return built || ""
    }, [docSign])

    const copyToClipboard = useCallback(
        async (text) => {
            if (!text) {
                enqueueSnackbar("URL não disponível para copiar.", { variant: "warning" })
                return
            }

            try {
                if (navigator?.clipboard?.writeText) {
                    await navigator.clipboard.writeText(text)
                } else {
                    const textarea = document.createElement("textarea")
                    textarea.value = text
                    textarea.style.position = "fixed"
                    textarea.style.opacity = "0"
                    document.body.appendChild(textarea)
                    textarea.focus()
                    textarea.select()
                    document.execCommand("copy")
                    textarea.remove()
                }

                enqueueSnackbar("URL copiada!", { variant: "success" })
            } catch (e) {
                enqueueSnackbar("Não foi possível copiar a URL.", { variant: "error" })
            }
        },
        [enqueueSnackbar]
    )

    // ✅ se existir URL por signatário, usa; senão usa do documento
    const resolveSignerUrl = useCallback(
        (signer) => {
            const signerUrl = signer?.url
            return signerUrl ? String(signerUrl) : resolveDocViewUrl()
        },
        [resolveDocViewUrl]
    )

    // ✅ cancelamento do documento (POST sign/cancel/<document_id>/)
    const canCancel = useMemo(() => {
        const hasId = Boolean(docSign?.document_id)
        const statusValue = String(docSign?.status?.value || "").toUpperCase()
        // regra: não cancela FINISHED (ajuste se quiser)
        return hasId && statusValue !== "FINISHED"
    }, [docSign])

    const handleCancelDocument = useCallback(async () => {
        const documentId = docSign?.document_id
        if (!documentId) {
            enqueueSnackbar("Este documento não possui document_id para cancelar.", { variant: "warning" })
            setConfirmCancelOpen(false)
            return
        }

        setCancelling(true)
        setError("")
        try {
            // envia por path (e também no body p/ compat)
            await client.post(CANCEL_ENDPOINT(documentId), { document_id: documentId })
            enqueueSnackbar("Documento cancelado com sucesso.", { variant: "success" })
            setConfirmCancelOpen(false)
            fetchDetail()
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Não foi possível cancelar o documento."
            enqueueSnackbar(msg, { variant: "error" })
        } finally {
            setCancelling(false)
        }
    }, [client, docSign, enqueueSnackbar, fetchDetail])

    return (
        <Grid container spacing={2} justifyContent="center" sx={{ flexGrow: 1, px: "1rem" }}>
            <Grid item xs={12} md={8}>
                <Card sx={{ mb: 2 }}>
                    <CardHeader
                        title={"Signatários"}
                        avatar={
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "999px",
                                    backgroundColor: "#e0235c",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Person />
                            </Box>
                        }
                        action={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                {docSign?.status?.value === "SENT" && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <AccessTime color={"warning"} sx={{ mr: 0.5 }} />
                                        <Typography variant="subtitle1" color={"gray"} sx={{ fontSize: "small" }}>
                                            {safeStr(docSign?.signed)}
                                        </Typography>
                                    </Box>
                                )}
                                {docSign?.status?.value === "FINISHED" && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <CheckCircle color={"success"} sx={{ mr: 0.5 }} />
                                        <Typography variant="subtitle1" color={"gray"} sx={{ fontSize: "small" }}>
                                            {safeStr(docSign?.signed)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        }
                        sx={{
                            "& .MuiCardHeader-action": {
                                alignSelf: "center",
                            },
                        }}
                    />

                    <Divider />

                    {pendingSigners.length === 0 ? (
                        <Typography variant="body2" sx={{ opacity: 0.75, p: 2 }}>
                            Nenhum signatário pendente.
                        </Typography>
                    ) : (
                        <TableContainer variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <b>Nome</b>
                                        </TableCell>
                                        <TableCell>
                                            <b>Email</b>
                                        </TableCell>
                                        <TableCell align="center">
                                            <b>Status</b>
                                        </TableCell>
                                        <TableCell align="center">
                                            <b>Ação</b>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {pendingSigners.map((s, idx) => {
                                        const urlToCopy = resolveSignerUrl(s)

                                        return (
                                            <TableRow key={`${s?.email || idx}-${idx}`}>
                                                <TableCell>{safeStr(s?.name)}</TableCell>
                                                <TableCell>{safeStr(s?.email)}</TableCell>

                                                <TableCell align="center">
                                                    {s?.status_id === "Signed" ? (
                                                        <Chip size="small" variant="outlined" color="success" label={s?.status} />
                                                    ) : s?.status_id === "Declined" ? (
                                                        <Chip variant="outlined" color="error" label={s?.status} />
                                                    ) : (
                                                        <Chip size="small" label={s?.status} />
                                                    )}
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Tooltip title={urlToCopy ? "Copiar link da assinatura" : "URL indisponível"}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => copyToClipboard(urlToCopy)}
                                                                disabled={!urlToCopy}
                                                            >
                                                                <ContentCopy fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Card>

                <DocSignHistory docId={docSign?.document_id} />
            </Grid>

            <Grid item xs={12} md={4}>
                {error ? (
                    <Alert
                        severity="error"
                        action={
                            <Button size="small" onClick={fetchDetail}>
                                Tentar novamente
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                ) : null}

                <Card sx={{ borderRadius: 2, mt: error ? 2 : 0 }}>
                    <CardHeader
                        avatar={
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "999px",
                                    backgroundColor: "#e0235c",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Description fontSize="small" />
                            </Box>
                        }
                        title={<Typography variant="h6" sx={{ fontWeight: 800 }}>{docSign?.name}</Typography>}
                        subheader={
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                Criado em {moment(docSign?.created_at).format("DD/MM/YYYY [às] HH:mm")}
                            </Typography>
                        }
                        action={
                            <>
                                <IconButton onClick={openMenu} size="small" aria-label="ações do documento">
                                    <MoreVert />
                                </IconButton>

                                <Menu
                                    anchorEl={menuAnchor}
                                    open={menuOpen}
                                    onClose={closeMenu}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <MenuItem onClick={openCancelConfirm} disabled={!canCancel || cancelling}>
                                        <ListItemIcon>
                                            <CancelIcon fontSize="small" />
                                        </ListItemIcon>
                                        Cancelar documento
                                    </MenuItem>
                                </Menu>
                            </>
                        }
                        sx={{
                            "& .MuiCardHeader-action": {
                                alignSelf: "center",
                            },
                        }}
                    />

                    <Divider />

                    <CardContent>
                        {loading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                    Carregando...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        { docSign?.doc && <Typography sx={{ fontWeight: 800 }}>Template</Typography> }
                                        { docSign?.doc && <Typography sx={{ mb: 1 }}>{safeStr(docSign?.doc)}</Typography> }

                                        { docSign?.rent_contract && <Typography sx={{ fontWeight: 800 }}>Contrato</Typography> }
                                        { docSign?.rent_contract && <Typography sx={{ mb: 2 }}>{safeStr(docSign?.rent_contract)}</Typography> }

                                        <Button
                                            fullWidth
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            disabled={!docSign?.document_id || downloading}
                                            onClick={handleDownload}
                                        >
                                            {downloading ? "Baixando..." : "Baixar arquivo"}
                                        </Button>

                                        {!docSign?.document_id && (
                                            <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.75 }}>
                                                O documento ainda não possui <b>document_id</b> para download.
                                            </Typography>
                                        )}


                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* ✅ Dialog de confirmação */}
                <Dialog open={confirmCancelOpen} onClose={closeCancelConfirm} maxWidth="xs" fullWidth>
                    <DialogTitle>Cancelar documento</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Tem certeza que deseja cancelar este documento?
                        </Typography>

                        {!canCancel && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Cancelamento indisponível (verifique o status ou document_id).
                            </Alert>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeCancelConfirm} disabled={cancelling}>
                            Voltar
                        </Button>
                        <Button
                            color="error"
                            variant="contained"
                            onClick={handleCancelDocument}
                            disabled={!canCancel || cancelling}
                            startIcon={<CancelIcon />}
                        >
                            {cancelling ? "Cancelando..." : "Cancelar documento"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        </Grid>
    )
}
